import { Component, ElementRef, EventEmitter, HostListener, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, merge, of } from 'rxjs';
import { delay, filter, take, takeUntil, tap } from 'rxjs/operators';
import { Placement } from '../../tooltip';
import { TooltipInfoIconComponent } from '../../tooltip/tooltip-info-icon/tooltip-info-icon.component';

@UntilDestroy()
@Component({
  selector: 'yat-button-with-embedded-tooltip',
  templateUrl: './button-with-embedded-tooltip.component.html',
  styleUrls: ['./button-with-embedded-tooltip.component.scss']
})
export class ButtonWithEmbeddedTooltipComponent {
  @Input()
  id: string;

  @Input()
  active: boolean;

  @Input()
  tooltipClass: string;

  @Input()
  tooltipContent: string | TemplateRef<any>;

  @Output()
  selectButton: EventEmitter<null> = new EventEmitter();

  @ViewChild('button')
  button: ElementRef<HTMLButtonElement>;

  @ViewChild(TooltipInfoIconComponent)
  tooltipIcon: TooltipInfoIconComponent;

  tooltipPlacement: Placement[] = ['top', 'right'];

  tooltipIsShown: boolean = false;

  private windowMouseMove$: Subject<MouseEvent> = new Subject();

  private windowClick$: Subject<MouseEvent> = new Subject();

  @HostListener('window:mousemove', ['$event'])
  windowMouseMove(event: MouseEvent): void {
    this.windowMouseMove$.next(event);
  }

  @HostListener('window:click', ['$event'])
  windowClick(event: MouseEvent): void {
    this.windowClick$.next(event);
  }

  enterAndShowTooltip(tooltipDelay: number): void {
    if (this.tooltipIsShown === true) {
      return;
    }

    this.button.nativeElement.classList.add('hover');
    const boundRect = this.button.nativeElement.getBoundingClientRect();
    const mouseMoveOutsideButton = this.windowMouseMove$.pipe(filter(event => this.mouseEventIsOutsideButton(event, boundRect)));
    const mouseClickInsideButton = this.windowClick$.pipe(filter(event => !this.mouseEventIsOutsideButton(event, boundRect)));

    of(null)
      .pipe(
        delay(tooltipDelay),
        tap(() => {
          this.tooltipIsShown = true;
          this.tooltipIcon.openTooltip();
        }),
        takeUntil(merge(mouseMoveOutsideButton, mouseClickInsideButton)),
        untilDestroyed(this)
      )
      .subscribe();

    mouseMoveOutsideButton
      .pipe(
        take(1),
        takeUntil(mouseClickInsideButton),
        tap(() => {
          this.tooltipIcon.closeTooltip();
          this.resetAfterClosingTooltip();
        }),
        untilDestroyed(this)
      )
      .subscribe();

    mouseClickInsideButton
      .pipe(
        take(1),
        takeUntil(mouseMoveOutsideButton),
        tap(() => {
          this.selectButton.next(null);
          this.resetAfterClosingTooltip();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private resetAfterClosingTooltip(): void {
    this.button.nativeElement.classList.remove('hover');
    this.tooltipIsShown = false;
  }

  private mouseEventIsOutsideButton(event: MouseEvent, boundRect: DOMRect): boolean {
    return (
      event.clientX < boundRect.left - 1 || event.clientX > boundRect.right + 1 || event.clientY < boundRect.top - 1 || event.clientY > boundRect.bottom + 1
    );
  }
}
